// Value contribution: how much of the value and revenue an output (or outcome) creates each part of the process
// produced, so the parts add up to the whole. Beside it, a node's value and revenue stay as the value it supports:
// everything that depends on it, which is why each step on the way to an output shows that output's full value.
//
// An output's value is shared between the actions that produce it: in proportion to their work (hours, or cost where
// no hours are known), weighted by how much of the output's flow passes through them, so a step on a detour that a
// few items take gets less than one every item passes through. Only value adding work earns a share (see WORK_TYPE):
// necessary work is the cost of doing business, and exception work and other waste are friction, so their share goes
// to the work that creates the value. Where none of the work on the way is value adding, it's shared by flow between
// all of it, so the value isn't lost from the totals.
//
// Each action's contribution is then shared between the roles performing it, and separately between the systems used
// on it, by their share of its hours: each role (and each system) on an action spends all its time, so evenly. Cost
// would give a higher-paid role more of the value for the same work. So the actions' contributions add up to the
// value, the roles' to the value of the work roles do, and the systems' to the value of the work systems are used on.
//
// Only the value a model creates itself is shared out in it: value a shared output receives from another model is
// contributed in that model, so totals across models add up without counting anything twice.
//
// Shared with linq-server (copied by its `npm run copy`), so it must only import from this folder.
import { ACTION_FREQUENCY_TYPE, EDGE_TYPE, NODE_SUPERTYPE, WORK_TYPE } from "./linqset-base"
import { getInsights, isSupplyChain, Linqset, LinqsetEdge, LinqsetNode, NodeInsights } from "./linqset-model"

const groupByTarget = (edges:LinqsetEdge[]) => {
    const map = new Map<string, LinqsetEdge[]>()
    edges.forEach(e => {
        const list = map.get(e.targetKey)
        if (list) list.push(e)
        else map.set(e.targetKey, [e])
    })
    return map
}

/**
 * The share of a node's flow passing through each action and information node before it: the node itself has all of
 * it, and each node passes its share back over the nodes before it by its previousNodeProportions. A node passes its
 * share back once everything arriving at it is in; nodes in a loop pass on what they have, once each.
 */
export const flowShares = (linqset:Linqset, start:LinqsetNode, nodes:LinqsetNode[], edges:LinqsetEdge[]):Map<string, number> => {
    const nodeMap = new Map(nodes.map(n => [n._key, n]))
    const incoming = groupByTarget(edges)
    const chainOf = (n:LinqsetNode) => {
        const proportions = getInsights(n, linqset)?.previousNodeProportions?.map || {}
        return (incoming.get(n._key) || [])
            .map(e => ({e, previous: nodeMap.get(e.sourceKey)}))
            .filter((i):i is {e:LinqsetEdge, previous:LinqsetNode} => !!i.previous && isSupplyChain(i.previous.superType) && !!proportions[i.e.sourceKey])
            .map(i => ({previous: i.previous, proportion: proportions[i.e.sourceKey] as number}))
    }

    // how many nodes each node will receive a share from
    const waiting = new Map<string, number>()
    const found = [start]
    const seen = new Set([start._key])
    for (let i = 0; i < found.length; i++) {
        chainOf(found[i]).forEach(({previous}) => {
            waiting.set(previous._key, (waiting.get(previous._key) || 0) + 1)
            if (!seen.has(previous._key)) {
                seen.add(previous._key)
                found.push(previous)
            }
        })
    }

    const shares = new Map<string, number>([[start._key, 1]])
    const passed = new Set<string>()
    const passBack = (n:LinqsetNode, ready:LinqsetNode[]) => {
        passed.add(n._key)
        const share = shares.get(n._key) || 0
        chainOf(n).forEach(({previous, proportion}) => {
            shares.set(previous._key, (shares.get(previous._key) || 0) + share*proportion)
            const left = (waiting.get(previous._key) || 0) - 1
            waiting.set(previous._key, left)
            if (left === 0 && !passed.has(previous._key)) ready.push(previous)
        })
    }
    const run = (first:LinqsetNode) => {
        const ready = [first]
        while (ready.length) {
            const n = ready.shift()!
            if (!passed.has(n._key)) passBack(n, ready)
        }
    }
    run(start)
    found.filter(n => !passed.has(n._key)).forEach(n => { if (!passed.has(n._key)) run(n) })
    return shares
}

// work that earns a share of the value: value adding, as the user said or LINQ read it (not exception work on a detour)
const addsValue = (insights:NodeInsights) => !insights.exceptionWork && (!insights.workKind || insights.workKind === WORK_TYPE.VALUE_ADDING || insights.workKind === WORK_TYPE.NORMAL)

// the work an action does: its hours, or its cost where no hours are known
const workOf = (insights:NodeInsights) => insights.hours || insights.allocatedCostsByType?.total || 0

const add = (insights:NodeInsights|undefined, value:number, revenue:number) => {
    if (!insights) return
    insights.contributedValue = (insights.contributedValue || 0) + value
    insights.contributedRevenue = (insights.contributedRevenue || 0) + revenue
}

// shares an amount between nodes by their hours on the action: each spends all its time on it, so evenly
const shareBetween = (keys:string[], value:number, revenue:number, insightsOf:(key:string) => NodeInsights|undefined) => {
    keys.forEach(k => add(insightsOf(k), value/keys.length, revenue/keys.length))
}

// the roles performing an action, and the systems automating it or used by those roles where they perform it
const performersOf = (nodes:LinqsetNode[], edges:LinqsetEdge[]) => {
    const nodeMap = new Map(nodes.map(n => [n._key, n]))
    const incoming = groupByTarget(edges)
    return (action:LinqsetNode) => {
        const into = incoming.get(action._key) || []
        const performs = into.filter(e => e.type === EDGE_TYPE.PERFORM && nodeMap.get(e.sourceKey)?.superType === NODE_SUPERTYPE.ROLE)
        const roles = [...new Set(performs.map(e => e.sourceKey))]
        const places = new Set(performs.map(e => e.sourceVisualisationKey).filter(k => !!k))
        const systems = [...new Set([
            ...into.filter(e => e.type === EDGE_TYPE.AUTOMATE).map(e => e.sourceKey),
            ...edges.filter(e => e.type === EDGE_TYPE.USEBY && places.has(e.targetVisualisationKey)).map(e => e.sourceKey),
        ])].filter(k => nodeMap.get(k)?.superType === NODE_SUPERTYPE.SYSTEM)
        return { roles, systems }
    }
}

/**
 * Shares the value and revenue a node (an output or outcome) creates in a model between the actions producing it,
 * and from each action between its roles and between its systems (see above).
 */
export const contributeValue = (linqset:Linqset, source:LinqsetNode, value:number, revenue:number, nodes:LinqsetNode[], edges:LinqsetEdge[]) => {
    if (!(value > 0) && !(revenue > 0)) return
    const nodeMap = new Map(nodes.map(n => [n._key, n]))
    const shares = flowShares(linqset, source, nodes, edges)

    const steps = [...shares.entries()]
        .map(([key, share]) => ({node: nodeMap.get(key), share}))
        .filter((a):a is {node:LinqsetNode, share:number} => !!a.node && a.node.superType === NODE_SUPERTYPE.ACTION && a.share > 0)
        .map(a => ({...a, insights: getInsights(a.node, linqset)}))
        .filter(a => !!a.insights)
    // nobody works where items wait, so a wait earns nothing, unless waiting is all there is on the way (so the value
    // isn't lost from the totals)
    const working = steps.filter(a => a.insights.workKind !== WORK_TYPE.WAITING)
    const actions = working.length ? working : steps
    if (!actions.length) return

    // the value adding work by its work and flow, or by flow alone where none of its work is known; with none, all the
    // work by flow
    const anyValueAdding = actions.some(a => addsValue(a.insights))
    const earns = (a:{insights:NodeInsights}) => !anyValueAdding || addsValue(a.insights)
    let weights = actions.map(a => !anyValueAdding ? a.share : earns(a) ? a.share*workOf(a.insights) : 0)
    if (weights.every(w => w <= 0)) weights = actions.map(a => earns(a) ? a.share : 0)
    const total = weights.reduce((t, w) => t + w, 0)
    if (!(total > 0)) return

    const performers = performersOf(nodes, edges)
    const insightsOf = (key:string) => {
        const n = nodeMap.get(key)
        return n ? getInsights(n, linqset) : undefined
    }
    actions.forEach((a, i) => {
        const part = weights[i]/total
        const v = value*part, r = revenue*part
        add(a.insights, v, r)

        const { roles, systems } = performers(a.node)
        shareBetween(roles, v, r, insightsOf)
        shareBetween(systems, v, r, insightsOf)
    })
}

// Value at risk: the value and revenue lost if a step, role or system stopped, because the items it handles have no
// other route to the output or outcome. A step's is its output's value times the share of the output's items that
// pass through it. Where routes join, a step that takes items from any of them (adding up its inputs) loses only the
// share that came the stopped way; one that needs an item from each (taking the fewest) loses them all. A role
// stops the steps no other role does, as another role on a step can carry on. A system stops every step it's used on,
// as a step's systems are all needed for it. Like contributions, it's of the value a model creates itself.

/**
 * The share of a node's items lost if the stopped nodes stop: all of them for a stopped node, and for any other the
 * share of what arrives that comes from stopped work. A loop back adds nothing, so each node is counted once.
 */
export const lostShare = (linqset:Linqset, start:LinqsetNode, nodes:LinqsetNode[], edges:LinqsetEdge[], stopped:Set<string>):number => {
    const nodeMap = new Map(nodes.map(n => [n._key, n]))
    const incoming = groupByTarget(edges)
    const known = new Map<string, number>()
    const visiting = new Set<string>()
    const lost = (n:LinqsetNode):number => {
        if (stopped.has(n._key)) return 1
        const memo = known.get(n._key)
        if (memo !== undefined) return memo
        if (visiting.has(n._key)) return 0
        visiting.add(n._key)
        const proportions = getInsights(n, linqset)?.previousNodeProportions?.map || {}
        const previous = (incoming.get(n._key) || [])
            .map(e => ({previous: nodeMap.get(e.sourceKey), proportion: proportions[e.sourceKey] as number}))
            .filter((p):p is {previous:LinqsetNode, proportion:number} => !!p.previous && isSupplyChain(p.previous.superType) && !!p.proportion)
        let share = 0
        if (previous.length) {
            share = n.properties?.actionProcessType === ACTION_FREQUENCY_TYPE.MINIMUM
                ? 1 - previous.reduce((kept, p) => kept*(1 - lost(p.previous)), 1)
                : previous.reduce((t, p) => t + p.proportion*lost(p.previous), 0)
        }
        visiting.delete(n._key)
        share = Math.min(1, Math.max(0, share))
        known.set(n._key, share)
        return share
    }
    return lost(start)
}

const addAtRisk = (insights:NodeInsights|undefined, value:number, revenue:number) => {
    if (!insights) return
    insights.atRiskValue = (insights.atRiskValue || 0) + value
    insights.atRiskRevenue = (insights.atRiskRevenue || 0) + revenue
}

/**
 * Adds the value and revenue a node (an output or outcome) creates in a model that each step, role and system on the
 * way to it puts at risk (see above).
 */
export const valueAtRisk = (linqset:Linqset, source:LinqsetNode, value:number, revenue:number, nodes:LinqsetNode[], edges:LinqsetEdge[]) => {
    if (!(value > 0) && !(revenue > 0)) return
    const nodeMap = new Map(nodes.map(n => [n._key, n]))
    const actions = [...flowShares(linqset, source, nodes, edges).entries()]
        .filter(([key, share]) => share > 0 && nodeMap.get(key)?.superType === NODE_SUPERTYPE.ACTION)
        .map(([key]) => nodeMap.get(key)!)
    if (!actions.length) return
    const insightsOf = (key:string) => {
        const n = nodeMap.get(key)
        return n ? getInsights(n, linqset) : undefined
    }
    const atRisk = (stopped:string[], key:string) => {
        const share = lostShare(linqset, source, nodes, edges, new Set(stopped))
        if (share > 0) addAtRisk(insightsOf(key), value*share, revenue*share)
    }

    // each step, the steps each role is the only role doing, and the steps each system is used on
    const performers = performersOf(nodes, edges)
    const soleRole = new Map<string, string[]>()
    const systemSteps = new Map<string, string[]>()
    actions.forEach(a => {
        atRisk([a._key], a._key)
        const { roles, systems } = performers(a)
        if (roles.length === 1) soleRole.set(roles[0], [...(soleRole.get(roles[0]) || []), a._key])
        systems.forEach(s => systemSteps.set(s, [...(systemSteps.get(s) || []), a._key]))
    })
    soleRole.forEach((stopped, key) => atRisk(stopped, key))
    systemSteps.forEach((stopped, key) => atRisk(stopped, key))
}
